/**
 * Client-side helper: extract `@consumes(@<name>)` annotations from a recipe body.
 *
 * This is a lightweight, regex-based projection of the server-side
 * `extractConsumes` extractor (`packages/grammar/src/extractors/consumes.ts`).
 * It is safe to run in a React render path and inside a Web Worker context.
 *
 * Design invariants:
 *   - No `eval`, no `new Function`, no dynamic `import`. Pure regex logic.
 *   - No imports from `apps/api` or any Node/Workers-runtime module.
 *   - O(n) in body length. Single-pass regex scan.
 *   - <2 ms on a 10 kB body.
 *
 * Grammar reference: cook-as-process-design.md §6.3
 *   @consumes(artefact=@<slug>, amount=<n><unit>, source-attempt=@ref(<path>)?)
 *   @consumes(attempt=@ref(<path>), amount=<n><unit>?)
 *
 * The client helper returns `{ name, kind }` where:
 *   - `name` is the slug (artefact form) or ref path (attempt form).
 *   - `kind` is "artefact" | "attempt" | "unknown".
 *
 * @module
 */

/** One entry returned by parseConsumes. */
export interface ConsumesEntry {
  /** The slug (artefact form) or the raw @ref path (attempt form). */
  name: string;
  /** Inferred artefact kind: "artefact", "attempt", or "unknown" when inference fails. */
  kind: string;
}

/**
 * Extract all `@consumes(@<name>)` annotations from `bodyMarkdown`.
 *
 * Each match returns `{ name, kind }`:
 *   - Artefact form `@consumes(artefact=@Slug, …)` → `{ name: "Slug", kind: "artefact" }`.
 *   - Attempt form `@consumes(attempt=@ref(path), …)` → `{ name: "path", kind: "attempt" }`.
 *   - Unrecognised paren content → `{ name: rawContent, kind: "unknown" }`.
 *
 * Returns an empty array for an empty body or a body with no @consumes annotations.
 */
export function parseConsumes(bodyMarkdown: string): ConsumesEntry[] {
  if (!bodyMarkdown) return [];

  const results: ConsumesEntry[] = [];

  // Match every @consumes(…) occurrence.
  // The regex is intentionally non-greedy and handles nested parens up to
  // one level deep (e.g. @ref(…) inside the outer parens). Since @ref paths
  // do not themselves contain parens, a simple [^)]* inside the inner paren
  // is sufficient.
  //
  // Pattern: @consumes( <content> )
  // where <content> may contain @ref(<inner>) — one level of nested parens.
  const CONSUMES_RE = /@consumes\(([^()]*(?:\([^()]*\)[^()]*)*)\)/gu;

  let match: RegExpExecArray | null;
  while ((match = CONSUMES_RE.exec(bodyMarkdown)) !== null) {
    const content = match[1] ?? "";
    results.push(parseConsumesContent(content));
  }

  return results;
}

/**
 * Parse the content inside @consumes(…) into a ConsumesEntry.
 */
function parseConsumesContent(content: string): ConsumesEntry {
  // Artefact form: artefact=@Slug
  const artefactMatch = /\bartefact\s*=\s*@([\w-]+)/u.exec(content);
  if (artefactMatch) {
    return { name: artefactMatch[1] ?? content, kind: "artefact" };
  }

  // Attempt form: attempt=@ref(path)
  const attemptMatch = /\battempt\s*=\s*@ref\(([^)]+)\)/u.exec(content);
  if (attemptMatch) {
    return { name: attemptMatch[1] ?? content, kind: "attempt" };
  }

  // Bare @Slug shorthand (informal usage — not in spec but be tolerant)
  const bareSlugMatch = /^@([\w-]+)\s*$/u.exec(content.trim());
  if (bareSlugMatch) {
    return { name: bareSlugMatch[1] ?? content, kind: "artefact" };
  }

  return { name: content.trim(), kind: "unknown" };
}
